import { useTranslation } from 'next-i18next';

import { LinksMirrorDownload } from '@/common/link-mirror-download';
import { CTA_FONT } from '@/components/trial/lp/trial-beta/components/cta-font';
import { useBetaInstallerUrl } from '@/hooks/use-beta-installer-url';
import { useTrackSoftwareDownload } from '@/hooks/use-track-software-download';

interface BetaDownloadButtonProps {
  /** `hero` sits above the fold on the LP; `welcome` replaces the form after a signup. */
  tone?: 'hero' | 'welcome';
  className?: string;
}

const BASE_CLASS = 'inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 uppercase tracking-wide transition';

/**
 * The beta installer CTA, shared by the LP hero and the post-signup welcome state.
 *
 * The URL comes from `BETA_INSTALLER_URL` through `/api/beta-installer-url`, so it is read per
 * request on the server and can be changed with `.env` + `pm2 reload` — no rebuild. The fetch is
 * enabled unconditionally here: every visitor of this LP is a beta prospect, so there is nothing
 * to gate on.
 *
 * The hook's three states are all handled, because two of them are not interchangeable: while the
 * fetch is still in flight (`undefined`) the button stays disabled rather than rendering a stable
 * installer it would then swap underneath the visitor; once it settles as `null` — the env var is
 * genuinely unset — it degrades to the official installer so the button never dead-ends.
 */
export function BetaDownloadButton({ tone = 'hero', className = '' }: BetaDownloadButtonProps) {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const betaUrl = useBetaInstallerUrl(true);
  const stableUrl = LinksMirrorDownload();
  const trackDownload = useTrackSoftwareDownload(`trial_beta_${tone}`);

  const isPending = betaUrl === undefined;
  const href = betaUrl ?? stableUrl;

  const palette = tone === 'welcome' ? { ...CTA_FONT, background: '#93FF18', color: '#0A0A0F', boxShadow: '0 0 24px #93FF1840' } : { ...CTA_FONT, background: '#93FF1815', color: '#93FF18', border: '1px solid #93FF1840' };

  if (isPending) {
    return (
      <span className={`${BASE_CLASS} cursor-wait opacity-60 ${className}`} style={palette} aria-busy='true'>
        {t('betaDownload.preparing')}
      </span>
    );
  }

  return (
    <a href={href} onClick={() => trackDownload(href)} className={`${BASE_CLASS} ${className}`} style={palette} data-beta-download>
      {t('betaDownload.cta')}
    </a>
  );
}
